export const DEFAULT_ADMIN_DATABASE = "global";
export const INCLUDE_PREFIX = "_include_";
export const POPULATE_PREFIX = "_populate_";

// AUTO-NOTIFICATION DEFAULTS
export const DEFAULT_AUTO_NOTIFY_THRESHOLD = 5;

// MEASUREMENT EVENT DETAIL TYPES
export const MEASUREMENT_NEW_DETAIL_TYPE = "measurement.new";
export const MEASUREMENT_CREATED_DETAIL_TYPE = "measurement.created";
export const MEASUREMENT_ENHANCED_DETAIL_TYPE = "measurement.enhanced";
export const MEASUREMENT_UPDATED_DETAIL_TYPE = "measurement.updated";

// MEASUREMENT EVENT SOURCE
export const MEASUREMENT_SERVICE_SOURCE = "measurement.service";
// The rules engine: turns raw sensor facts into prioritized measurements.
// Must be present in the measurement-processing allowlist for these to be
// persisted; adding the source here alone routes nothing.
export const RULES_ENGINE_SOURCE = "rules.engine";

// GEMINI MODEL DEFAULTS
export const DEFAULT_GEMINI_MODEL = "gemini-3.5-flash-lite";

// GEMINI PRICING (per million tokens, USD)
// KEEP IN SYNC with packages/ai-vlm/pricing.py — the Python worker is the
// runtime source of truth for cost values emitted on vlm.usage events.
// Bump GEMINI_PRICING_VERSION in both sides together when rates change.
interface ModelPricing {
	readonly inputPerMillion: number;
	readonly longContextInputPerMillion?: number;
	readonly longContextOutputPerMillion?: number;
	readonly longContextThresholdTokens?: number;
	readonly outputPerMillion: number;
}

export const GEMINI_PRICING_VERSION = "2026-09";

export const GEMINI_PRICING: Readonly<Record<string, ModelPricing>> = {
	"gemini-3.1-flash-lite": {
		inputPerMillion: 0.25,
		outputPerMillion: 1.5,
	},
	"gemini-3.1-pro-preview": {
		inputPerMillion: 2.0,
		longContextInputPerMillion: 4.0,
		longContextOutputPerMillion: 18.0,
		longContextThresholdTokens: 200_000,
		outputPerMillion: 12.0,
	},
	"gemini-3.5-flash": {
		inputPerMillion: 1.5,
		outputPerMillion: 9.0,
	},
	"gemini-3.5-flash-lite": {
		inputPerMillion: 0.3,
		outputPerMillion: 2.5,
	},
	// Verified 2026-09-06 against https://ai.google.dev/gemini-api/docs/pricing.
	// Introductory rates through 2026-12-31; Google lists $1.50 / $7.50 from
	// 2027-01-01. Output includes thinking tokens. No long-context tier listed.
	"gemini-3.8-flash": {
		inputPerMillion: 0.75,
		outputPerMillion: 3.75,
	},
};

// GEMINI INLINE REQUEST LIMIT
// Applies to the BASE64-ENCODED request payload (media parts plus prompt), not
// to the raw clip bytes — base64 inflates raw bytes by 4/3, so the practical
// raw ceiling is ~75 MB. The Python VLM worker applies the same cap; it reads
// these two values from its ECS task environment (infra/ai-services.ts), so
// this is the single place to change them.
export const GEMINI_INLINE_REQUEST_LIMIT_BYTES = 100 * 1024 * 1024;
// Room reserved for the prompt text and JSON envelope that ride alongside the
// media parts in the same request. The one allowance for everything that is
// not media — callers do not budget the prompt separately.
export const GEMINI_INLINE_ENVELOPE_HEADROOM_BYTES = 512 * 1024;

// PLAUSIBILITY VERIFICATION
// Hand-bumped whenever any of the three prompt bodies or their response
// schemas change (precedent: GEMINI_PRICING_VERSION), and when the anchored
// model changes, since a new model's observations carry different semantics.
// Stamped on every Verification so replay and calibration pool only rows
// produced by the same wording and model.
export const PLAUSIBILITY_PROMPT_VERSION = "2026-09.5";
/**
 * The anchored pass moved from flash-lite to 3.8 Flash on 2026-09-14, ahead
 * of the runbook's 30-verdict rule, with the side-by-side kept on (now running
 * flash-lite) so calibration keeps comparing the two. Prompted by pete-test-001
 * verification cddddc13: flash-lite read a customer walking in with a puppy
 * as merchandise carried out of the door and scored 65% (alert now); the same
 * row replayed with 3.8's checklist scores 0.08% (low-priority log). See
 * docs/runbooks/anchored-model-decision.md.
 */
export const PLAUSIBILITY_ANCHORED_MODEL = "gemini-3.8-flash";
export const PLAUSIBILITY_CLASSIFIER_MODEL = DEFAULT_GEMINI_MODEL;
/**
 * The blind pass runs on a different, stronger model than Stage 1's so the
 * second opinion is independent rather than the same model repeating itself.
 * Must be a key in GEMINI_PRICING or the usage rows carry no cost.
 */
/**
 * The blind pass moved off `gemini-3.1-pro-preview` on 2026-09-07. Pro at the
 * API's default (low) media resolution was the weakest reader in the chain:
 * on the K&G walkway clip it called an ordinary walk into a store a decisive
 * altercation on every run, outvoting an anchored pass that had the frames to
 * see otherwise. 3.8 Flash at high resolution costs what Pro cost at low
 * (~$0.014 a verification), so the swap buys the pixels rather than paying
 * for them. The anchored pass was kept on a different model so this pass was
 * not the one grading its own earlier answer; since 2026-09-14 both run on
 * 3.8 Flash (see PLAUSIBILITY_ANCHORED_MODEL). This pass is still shown none
 * of the anchored prompt, candidate or output, but the model is no longer
 * independent — the main argument for moving the anchored pass back.
 */
export const PLAUSIBILITY_BLIND_MODEL = "gemini-3.8-flash";
/** Per-frame detail for the blind clip part. See the note above. */
export const PLAUSIBILITY_BLIND_MEDIA_RESOLUTION = "high" as const;
/**
 * Anchored-pass side-by-side. While enabled, every verification also runs the
 * anchored checklist on this model, after the real pass, and stores the
 * result on `verification.anchoredShadow`. Nothing it produces feeds a score,
 * a decision, or a severity; the nightly calibration report compares both
 * models' observations against verdicts. Since 2026-09-14 this is flash-lite,
 * the anchored pass's previous model, so the comparison now asks whether to
 * stay on 3.8 Flash. Pinned to an id rather than DEFAULT_GEMINI_MODEL so a
 * change of default cannot silently change what is being compared. Decision
 * rule and procedure: docs/runbooks/anchored-model-decision.md. Turn off once
 * decided.
 */
export const PLAUSIBILITY_ANCHORED_SHADOW_MODEL = "gemini-3.5-flash-lite";
export const PLAUSIBILITY_ANCHORED_SHADOW_ENABLED = true;
export const PLAUSIBILITY_BLIND_TOP_K = 3;
/**
 * The anchored pass looks at the stretch Stage 1 flagged, not the whole clip,
 * and at this many frames per second instead of Gemini's default of one.
 * Its features are motion (a swing, a fall, a retreat), and at 1 fps two
 * seconds of people walking in contact is two frames of "bodies touching",
 * which reads as grappling. Cameras record at about 5 fps, so more than this
 * only duplicates frames. The blind pass keeps the whole clip at 1 fps: it
 * ranks classes rather than measuring motion, and it runs on the pro model.
 */
export const PLAUSIBILITY_ANCHORED_WINDOW_FPS = 5;
/** Seconds added before and after the Stage 1 window. */
export const PLAUSIBILITY_ANCHORED_WINDOW_PAD_SECONDS = 3;
/**
 * Frame budget for the windowed anchored pass: 30 s at 5 fps, about twice the
 * frames of a whole minute at 1 fps. A longer window lowers the rate to stay
 * inside it, so a Stage 1 window spanning the whole clip cannot quietly turn
 * into a five-times-larger request.
 */
export const PLAUSIBILITY_ANCHORED_WINDOW_MAX_FRAMES = 150;
/**
 * Per-frame detail for the anchored pass. Gemini's default for video is
 * "low", about 60 tokens a frame — a thumbnail. Two people overlapping at the
 * far edge of a fisheye frame are a blob at that size, and more frames of a
 * blob (the 5 fps window above) did not help on the K&G walkway clip. "high"
 * is about four times the video tokens, on the cheap model only.
 */
export const PLAUSIBILITY_ANCHORED_MEDIA_RESOLUTION = "high" as const;
/**
 * Live mode rewrites measurement severity and holds 5+ notifications for the
 * verdict. Flipped on 2026-09-06 once the notification hold shipped and the
 * first shadow verifications read correctly; a tenant still has to be set to
 * `live` by staff before any of it applies to them.
 */
export const PLAUSIBILITY_LIVE_MODE_ENABLED = true;
/**
 * Observability-only event the verifier emits when a verification completes.
 * Distinct source so nothing on the tenant chain can ever consume it by
 * accident (the same isolation the eval pipeline relies on).
 */
export const PLAUSIBILITY_VERIFIER_SOURCE = "plausibility.verifier";
export const VERIFICATION_COMPLETED_DETAIL_TYPE = "verification.completed";

/**
 * Staff reanalysis (`docs/plausibility-verification.md` §12). The request
 * routes emit `reanalysis.requested` from this source and the reanalysis
 * worker takes it from its own queue: the evidence passes are video calls
 * that can outlast API Gateway's 30 s limit.
 */
export const REANALYSIS_SOURCE = "reanalysis.service";
export const REANALYSIS_REQUESTED_DETAIL_TYPE = "reanalysis.requested";
/**
 * Most measurements one reanalysis runs the evidence passes on. A false alarm
 * has to down-rank every alert-level measurement holding the event's severity
 * up (the event is the max of its measurements); past this many the event is
 * not one misread clip, and the bill should not grow with it.
 */
export const REANALYSIS_MAX_MEASUREMENTS = 5;
/**
 * How long a reanalysis may stay `queued`/`running` before a new request for
 * the same event treats it as dead and marks it failed. Just past the queue's
 * 30 min visibility timeout, as `SCENE_FACTS_DRAFT_STUCK_MS` is: a worker that
 * died mid-run leaves the row open and its message in the DLQ, and without
 * this the event could never be reanalyzed again.
 */
export const REANALYSIS_STUCK_MS = 35 * 60 * 1000;

// EVENT TYPES
export const UNCATEGORIZED_EVENT_TYPE = "uncategorized";

// MONGO QUERY DEFAULTS
export const MONGO_LIMIT = 50;
export const MONGO_SORT_FIELD = "_cursor";

// SENSOR HEALTH THRESHOLDS
export const SENSOR_WARNING_THRESHOLD_MS = 10 * 60 * 1000;
export const SENSOR_OFFLINE_THRESHOLD_MS = 30 * 60 * 1000;

// SCENE DESCRIPTION (per-camera background context — see sitescene#460)
/**
 * How long a new camera's sampling window stays open before generation runs.
 * A full day so the generator sees the scene under daylight, glare, dusk, and
 * IR night, rather than inferring permanent structure from one lighting state.
 */
export const SCENE_SAMPLING_WINDOW_MS = 24 * 60 * 60 * 1000;
/**
 * Clips listed from S3 for one final generation. A ceiling on the LIST, not on
 * frames — the generator then spreads its frame selection across whatever came
 * back. Generous because a busy camera can produce hundreds of clips a day and
 * an even spread across all of them is the point.
 */
export const SCENE_SAMPLING_MAX_CLIPS = 500;
/** Frames sent to the model, spread evenly across the sampling window. */
export const SCENE_DESCRIPTION_FRAME_COUNT = 4;
/**
 * Frames behind a preliminary description. One, by definition: it exists to say
 * something useful about a camera within a minute of its first clip, before
 * there is any history to spread across. The single-lighting-state weakness
 * this implies is exactly why it is labelled `Preliminary` and replaced
 * unattended a day later.
 */
export const SCENE_DESCRIPTION_PRELIMINARY_FRAME_COUNT = 1;
/**
 * How far back a preliminary generation looks. The triggering clip landed
 * seconds ago; this only needs to be wide enough to absorb event-delivery lag.
 */
export const SCENE_DESCRIPTION_PRELIMINARY_LOOKBACK_MS = 60 * 60 * 1000;
/**
 * Strong model used for generation only. Deliberately not the analyzer's
 * `DEFAULT_GEMINI_MODEL` — this runs once per camera, so accuracy outranks
 * cost. Must be a key in `GEMINI_PRICING`.
 */
export const SCENE_DESCRIPTION_MODEL = "gemini-3.1-pro-preview";
/**
 * Camera geometry paragraph: mounting height, tilt, lens, near/far, apparent
 * size of a person near versus far. A property of the camera rather than the
 * scene, so it is generated from the first clip and injected right away,
 * appended after the scene description. Research: naming the geometry cut
 * steep-angle false alerts by about three quarters with recall unchanged.
 */
export const SCENE_GEOMETRY_MAX_CHARS = 700;
/**
 * The generator is not deterministic, so it is run this many times and the
 * most conservative draft is kept (`selectConservativeGeometry`).
 */
export const SCENE_GEOMETRY_DRAFT_COUNT = 3;
/**
 * Sensors processed per tenant per cron tick.
 *
 * Sized against real per-sensor cost, not a round number: each sensor means up
 * to `SCENE_DESCRIPTION_FRAME_COUNT` clip downloads, the same number of ffmpeg
 * invocations, and one multi-image call to a thinking model — call it 20-60s.
 * At four, a tenant's batch fits comfortably inside
 * `SCENE_DESCRIPTION_TIME_BUDGET_MS` and leaves room for the tenants behind it.
 * Backlogs drain over subsequent ticks; the hourly cadence is the throttle.
 */
export const SCENE_DESCRIPTION_BATCH_SIZE = 4;
/**
 * Wall-clock budget for one cron invocation, checked between tenants and
 * between sensors. Set below the Lambda's 300s timeout so the run ends by
 * choosing to stop, leaving a clean summary log, rather than by being killed
 * mid-write.
 */
export const SCENE_DESCRIPTION_TIME_BUDGET_MS = 240 * 1000;
/**
 * Generation attempts allowed against one sampling window before it is
 * abandoned. The candidate set is frozen once the window closes, so a third
 * failure is running the same frames through the same model for the third
 * time — past this point retrying is spend, not recovery.
 */
export const SCENE_DESCRIPTION_MAX_ATTEMPTS = 3;
/**
 * Minimum gap between attempts on the same window. Long enough that a transient
 * outage has cleared, short enough that a camera still gets described the same
 * day. Also the mechanism that stops a failing sensor from re-entering the
 * batch on the very next tick and crowding out untried ones.
 */
export const SCENE_DESCRIPTION_RETRY_BACKOFF_MS = 6 * 60 * 60 * 1000;
/**
 * Hard cap on a stored description. This text rides on every subsequent
 * analyzer call, so an overlong description dilutes the task instructions.
 */
export const SCENE_DESCRIPTION_MAX_CHARS = 1000;

// SCENE FACTS (human-taught layout facts per camera — see docs/scene-facts.md)
/**
 * Hard cap on a camera's stored scene facts. Rides after the description and
 * geometry on every analyzer call, so it is kept well under the description's
 * own cap: a few sentences on what lies beyond the frame edges and where the
 * exits are, not a second description.
 */
export const SCENE_FACTS_MAX_CHARS = 600;
/**
 * Model that drafts scene facts from a note and the clip. Human-triggered and
 * rare, so it can afford the stronger reader; the same model the plausibility
 * blind pass runs on. Must be a key in `GEMINI_PRICING`.
 */
export const SCENE_FACTS_MODEL = "gemini-3.8-flash";
/**
 * How long a proposal may sit in `drafting` before staff can redraft it. A
 * draft worker that dies (its 300 s timeout) leaves the message to be
 * redelivered after the SceneFactsQueue's 30 min visibility timeout, and after
 * its last receive the message goes to the DLQ with the proposal still
 * `drafting`. Just past one visibility window, so a draft still in flight is
 * almost never re-queued under it.
 */
export const SCENE_FACTS_DRAFT_STUCK_MS = 35 * 60 * 1000;

// PERMISSIONS
/**
 * Canonical permission catalog. Derived from the Permissions & User Management
 * spec (§3.6 component state table + §2.2 granular permission logic).
 *
 * Values follow the `resource:action` convention used by Kinde; the
 * PascalCase keys are human-readable labels for use in app code.
 */
export const PERMISSIONS = {
	AccessLocations: "location:access",
	AccessUsers: "user:access",
	AccessWatches: "watch:access",
	/**
	 * Copy an event from one tenant's database into another
	 * (`POST /admin/copy-event`). Internal staff only — a holder can read
	 * events out of ANY tenant by id, so it is deliberately excluded from the
	 * role templates and the user-management grid.
	 */
	CopyEvent: "event:copy",
	CreateLocation: "location:create",
	CreateWatch: "watch:create",
	EditEventDetails: "event:write",
	EditEventType: "event:change_type",
	EditLocationDetails: "location:update",
	EditSensorDetails: "sensor:update",
	EditUserDetails: "user:update",
	EditWatchDetails: "watch:update",
	EditZoneDetails: "zone:update",
	ExportData: "event:export",
	InviteUsers: "user:invite",
	/**
	 * Staff reanalysis of one event (`/events/{eventId}/reanalysis/*`): say what
	 * the clip really showed and let the plausibility verifier re-decide the
	 * event against it. Internal staff only — it rewrites a customer's event —
	 * so, like `event:copy`, it is granted in Kinde and never by a role template.
	 */
	ReanalyzeEvent: "event:reanalyze",
	/** In-app "Report a problem" control. Granted in Kinde only — not the user-edit grid. */
	ReportProblem: "feedback:report",
	ResolveEvents: "event:resolve",
	SendNotifications: "notification:send",
	ViewActivityLogs: "event:activity",
	ViewOperations: "event:view_operations",
	ViewSafety: "event:view_safety",
	ViewSecurity: "event:view_security",
} as const;

/**
 * Permission keys recognised by the platform. Derived from
 * {@link PERMISSIONS} so adding/renaming a catalog entry automatically
 * flows through to every `Permission`-typed usage — no manual union
 * to keep in sync.
 */
export type Permission = (typeof PERMISSIONS)[keyof typeof PERMISSIONS];

/**
 * Role templates used only for UI prefill. Backend never reads roles —
 * it accepts the flat permissions array submitted by the client.
 */
export type RoleTemplate =
	| "admin"
	| "manager"
	| "operator"
	| "responder"
	| "view_only";

/**
 * Complete list of permissions as an array. Useful for iterating when
 * rendering the user-management permission grid or seeding Kinde.
 */
export const ALL_PERMISSIONS: readonly Permission[] =
	Object.values(PERMISSIONS);

/**
 * Parent/child relationships for cascade enforcement (spec §2.3.3).
 *
 * A child permission is only meaningful when its parent is also granted.
 * Server-side `applyCascade` strips any child whose parent is missing;
 * the UI mirrors this by auto-unchecking children when the parent is
 * unchecked.
 */
export const PERMISSION_TREE: Record<Permission, null | Permission> = {
	"event:activity": null,
	"event:change_type": null,
	"event:copy": null,
	"event:export": null,
	"event:reanalyze": null,
	"event:resolve": null,
	"event:view_operations": null,
	"event:view_safety": null,
	"event:view_security": null,
	"event:write": null,
	"feedback:report": null,
	"location:access": null,
	"location:create": "location:access",
	"location:update": "location:access",
	"notification:send": null,
	"sensor:update": "location:access",
	"user:access": null,
	"user:invite": "user:access",
	"user:update": "user:access",
	"watch:access": null,
	"watch:create": "watch:access",
	"watch:update": "watch:access",
	"zone:update": "location:access",
};

/**
 * True when `userPermissions` includes `permission` in the JWT flat list.
 *
 * {@link PERMISSION_TREE} defines parent requirements when granting permissions
 * (a child is only valid when its parent is also held). It does not mean holding
 * a child grants the parent — e.g. `watch:create` alone does not grant
 * `watch:access`.
 */
export function userHasPermission(
	userPermissions: readonly Permission[],
	permission: Permission,
): boolean {
	return userPermissions.includes(permission);
}

/**
 * Human-readable labels for each permission. Used by the user-management
 * form to render one checkbox per catalog key.
 */
export const PERMISSION_LABELS: Record<Permission, string> = {
	"event:activity": "View All Activity Logs",
	"event:change_type": "Change Event Type",
	"event:copy": "Copy Events Between Tenants",
	"event:export": "Export Event Data",
	"event:reanalyze": "Reanalyze Events",
	"event:resolve": "Resolve & Reopen Events",
	"event:view_operations": "View Operations Events",
	"event:view_safety": "View Safety Events",
	"event:view_security": "View Security Events",
	"event:write": "Edit Event Details",
	"feedback:report": "Report a Problem",
	"location:access": "Access Location Management",
	"location:create": "Create Location",
	"location:update": "Edit Location Details",
	"notification:send": "Send Notifications",
	"sensor:update": "Edit Sensor Details",
	"user:access": "Access User Management",
	"user:invite": "Invite Users",
	"user:update": "Edit User Details",
	"watch:access": "Access Watches",
	"watch:create": "Create Watch",
	"watch:update": "Edit Watch",
	"zone:update": "Edit Zone Details",
};

/**
 * Logical groupings for the user-management permission grid. The form
 * renders one section per key; order within a section matches design.
 */
export const PERMISSION_GROUPS: {
	label: string;
	permissions: Permission[];
}[] = [
	{
		label: "Event Type Visibility",
		permissions: [
			"event:view_security",
			"event:view_safety",
			"event:view_operations",
		],
	},
	{
		label: "Event Actions",
		permissions: [
			"event:resolve",
			"event:write",
			"event:change_type",
			"notification:send",
		],
	},
	{
		label: "Data",
		permissions: ["event:export", "event:activity"],
	},
	{
		label: "Administration",
		permissions: [
			"location:access",
			"location:create",
			"location:update",
			"zone:update",
			"sensor:update",
			"user:access",
			"user:update",
			"user:invite",
		],
	},
	{
		label: "Watches",
		permissions: ["watch:access", "watch:create", "watch:update"],
	},
];

/**
 * Permissions assigned only in Kinde. They are omitted from role templates
 * and the user-edit grid so saving a user cannot add or remove them.
 */
export const KINDE_MANAGED_PERMISSIONS: readonly Permission[] = [
	PERMISSIONS.CopyEvent,
	PERMISSIONS.ReanalyzeEvent,
	PERMISSIONS.ReportProblem,
];

export function isKindeManagedPermission(permission: string): boolean {
	return (KINDE_MANAGED_PERMISSIONS as readonly string[]).includes(permission);
}

export function withoutKindeManagedPermissions(
	permissions: readonly string[],
): string[] {
	return permissions.filter(
		(permission) => !isKindeManagedPermission(permission),
	);
}

/**
 * Keep Kinde-managed keys at their current values. The dashboard form never
 * authors these, so a full-replace save must not grant or revoke them.
 */
export function withPreservedKindeManagedPermissions(
	submitted: Permission[],
	current: readonly Permission[],
): Permission[] {
	const next = submitted.filter(
		(permission) => !isKindeManagedPermission(permission),
	);
	for (const permission of KINDE_MANAGED_PERMISSIONS) {
		if (current.includes(permission)) {
			next.push(permission);
		}
	}
	return next;
}

/**
 * Role → permission set mapping. UI prefill only — the backend never
 * expands or reads roles; it validates the flat permission array sent
 * by the client against `applyCascade` / `assertSubset` / `assertParity`.
 */
export const ROLE_TEMPLATES: Record<RoleTemplate, Permission[]> = {
	// `event:copy` reads across tenant boundaries, so it is withheld from the
	// admin template — a customer's own admin must not get it by default. It is
	// granted directly in Kinde to internal staff accounts. Other
	// {@link KINDE_MANAGED_PERMISSIONS} are withheld for the same reason.
	admin: ALL_PERMISSIONS.filter(
		(permission) => !isKindeManagedPermission(permission),
	),
	manager: [
		"event:view_security",
		"event:view_safety",
		"event:view_operations",
		"event:activity",
		"event:resolve",
		"event:write",
		"event:change_type",
		"notification:send",
		"event:export",
		"location:access",
		"location:update",
		"zone:update",
		"sensor:update",
		"user:access",
		"user:update",
		"user:invite",
		"watch:access",
		"watch:create",
		"watch:update",
	],
	operator: [
		"event:view_security",
		"event:view_safety",
		"event:view_operations",
		"event:activity",
		"event:resolve",
		"event:write",
		"notification:send",
		"watch:access",
		"watch:create",
		"watch:update",
	],
	responder: [
		"event:view_security",
		"event:view_safety",
		"event:view_operations",
		"event:resolve",
		"notification:send",
	],
	view_only: [
		"event:view_security",
		"event:view_safety",
		"event:view_operations",
	],
};

/**
 * Short helper descriptions shown next to the role name in the role
 * dropdown on the user-management form.
 */
export const ROLE_TEMPLATE_DESCRIPTIONS: Record<RoleTemplate, string> = {
	admin: "Full system access across all locations.",
	manager: "Administrative control scoped to assigned locations.",
	operator: "Monitor alerts and handle day-to-day response.",
	responder: "Verify alerts, resolve events, and send notifications.",
	view_only: "Read-only access to the alerts dashboard.",
};
